import { bindable } from 'aurelia-framework';

const ENTRIES_INTERVAL_TIME = 3000;

class JobContainer {
  @bindable selectedAccount = null;
  @bindable selectedJob = null;
  @bindable route = '';
  @bindable onError = () => {};
  @bindable onCancel = () => {};
  @bindable onFinish = () => {};
  loading = false;
  subLoading = false;
  running = false;
  log = '';
  done = false;
  cancelled = false;
  _visible = false;
  _currentJobEntry = -1;
  _currentRequest = null;
  _visible = false;
  /**
   * @param {AppAPI}            appAPI            To be able to call api errors.
   * @param {AppErrorPublisher} appErrorPublisher To publish errors to the UI.
   * @param {errorParser}       errorParser       Used for error parsing.
   * @param {ProductFeedsJobs}  productFeedsJobs  To get the jobs.
   */
  constructor(
    appAPI,
    appErrorPublisher,
    errorParser,
    productFeedsJobs,
  ) {
    'inject';

    /**
     * A local reference for the `appAPI` service.
     * @type {AppAPI}
     * @access protected
     */
    this._appAPI = appAPI;
    /**
     * The parser the container will use to format possible errors.
     * @type {ParserrorWrapper}
     * @access protected
     * @ignore
     */
    this._jobErrorParser = errorParser.wrap();
    this._appErrorPublisher = appErrorPublisher;
    this._productFeedsJobs = productFeedsJobs;
  }
  attached() {
    this._visible = true;
  }
  cancelJob() {
    this.cancelled = true;
    if (this._currentRequest) {
      this._currentRequest.abort();
    }

    this._productFeedsJobs.cancelJob(
      this.selectedAccount.id,
      this.selectedJob.id,
    )
    .then(() => {
      this.onCancel();
    })
    .catch((error) => {
      const parsedError = this._jobErrorParser(error, 'There was an error cancelling the job');

      this._appErrorPublisher.notify(error, parsedError.message);
    });
  }
  detached() {
    this._visible = false;
    if (this._currentRequest) {
      this._currentRequest.abort();
    }
  }
  selectedAccountChanged(currentAccount, previousAccount) {
    if (!previousAccount || currentAccount.id !== previousAccount.id) {
      this._loadInformation();
    }
  }
  selectedJobChanged() {
    this._loadInformation();
  }
  _loadInformation() {
    if (this.selectedAccount && this.selectedJob) {
      this.log = '';
      this.cancelled = false;
      this.running = this.selectedJob.status === 'running';
      if (this.running) {
        this._loadRunningJobLog();
      } else {
        this._loadJobLog();
      }
    }
  }
  _loadJobLog() {
    this.loading = true;
    this._productFeedsJobs.getJobLog(
      this.selectedAccount.id,
      this.selectedJob.id,
    )
    .then((log) => {
      this.log = log;
    })
    .catch((error) => (
      error.response.json()
      .catch(() => ({
        message: 'Unable to load the job log',
      }))
      .then((errorResponse) => Promise.reject(this._appAPI.error(
        errorResponse,
        error.response.status,
      )))
    ))
    .catch((error) => {
      const parsedError = this._jobErrorParser(error, 'There was an error loading the job log');

      this._appErrorPublisher.notify(error, parsedError.message);
      this.onError();
    })
    .then(() => {
      this.loading = false;
    });
  }
  _loadNextEntries() {
    if (this._visible) {
      this.subLoading = true;
      this._currentRequest = this._productFeedsJobs.getJobEntries(
        this.selectedAccount.id,
        this.selectedJob.id,
        this._currentJobEntry,
        true,
      )
      .then((response) => {
        this._currentRequest = null;
        if (this._visible && !this.cancelled) {
          if (response.log) {
            this.log = `${this.log}\n${response.log}`;
          }
          if (response.done) {
            this.done = true;
            const { status } = response;
            this.log = `${this.log}\n\nFinished: ${status.toUpperCase()}`;
            this.onFinish({ status });
          } else {
            this._currentJobEntry = response.entry;
            setTimeout(() => this._loadNextEntries(), ENTRIES_INTERVAL_TIME);
          }
        } else if (this.cancelled) {
          this.log = `${this.log}\n\nCanceled`;
        }
      })
      .catch((error) => {
        this._currentRequest = null;
        if (!error.aborted && this._visible) {
          const parsedError = this._jobErrorParser(error, 'There was an error loading the rest of the job log');

          this._appErrorPublisher.notify(error, parsedError.message);
        } else if (!error.aborted && !this._visible) {
          this._appErrorPublisher.publish(error);
        }
      })
      .then(() => {
        this.loading = false;
      });
    }
  }
  _loadRunningJobLog() {
    this._currentJobEntry = 1;
    this.loading = true;
    this._currentRequest = this._productFeedsJobs.getJobEntries(
      this.selectedAccount.id,
      this.selectedJob.id,
      this._currentJobEntry,
      false,
      true,
    )
    .then((response) => {
      this._currentRequest = null;
      if (this._visible && !this.cancelled) {
        this._currentJobEntry = response.entry;
        this.log = response.log;
        setTimeout(() => this._loadNextEntries(), ENTRIES_INTERVAL_TIME);
      } else if (this.cancelled) {
        this.log = 'Cancelled';
      }
    })
    .catch((error) => {
      if (!error.aborted && this._visible) {
        const parsedError = this._jobErrorParser(error, 'There was an error loading the job log');

        this._appErrorPublisher.notify(error, parsedError.message);
        this.onError();
      }
    })
    .then(() => {
      this.loading = false;
    });
  }
}

export { JobContainer };
